// Tickets Queue Management Page
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  Select,
  Card,
  CardContent,
  Badge,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  Search,
  Refresh,
  Assignment,
  AssignmentInd,
  AccessTime,
  PriorityHigh,
} from '@mui/icons-material';
import { DataTable, type Column } from '@/components/common/DataTable';
import { NewTicketDialog } from '@/components/tickets/NewTicketDialog';
import { useOrganization } from '@/contexts/OrganizationContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/contexts/ToastContext';
import { ticketsService } from '@/services/collections/tickets';
import { formatRelativeTime } from '@/services/firebase/firestore';
import type { Ticket, TicketStatus, TicketPriority, TicketType } from '@/types';

const STATUS_COLORS: Record<TicketStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  new: 'info',
  open: 'warning',
  pending: 'default',
  resolved: 'success',
  closed: 'default',
};

const PRIORITY_COLORS: Record<TicketPriority, 'default' | 'info' | 'warning' | 'error'> = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  urgent: 'error',
};

const TYPE_LABELS: Record<TicketType, string> = {
  bug: 'Bug',
  feedback: 'Feedback',
  feature_request: 'Feature',
  support: 'Support',
  question: 'Question',
};

export const TicketsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { organization, project } = useOrganization();
  const { canManageTickets } = usePermissions();
  const { showSuccess, showError } = useToast();

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<TicketType | 'all'>('all');
  const [newTicketOpen, setNewTicketOpen] = useState(false);

  // Fetch tickets
  const fetchTickets = async () => {
    if (!organization || !project) return;

    setLoading(true);
    try {
      let data: Ticket[] = [];

      switch (tab) {
        case 0: // All Open
          data = await ticketsService.getOpenTickets(organization.id, project.id);
          break;
        case 1: // Unassigned
          data = await ticketsService.getUnassigned(organization.id, project.id);
          break;
        case 2: // My Tickets
          // TODO: Filter by current user
          data = await ticketsService.getOpenTickets(organization.id, project.id);
          break;
        case 3: // Resolved
          data = await ticketsService.getByStatus(organization.id, project.id, 'resolved');
          break;
        case 4: // All
          data = await ticketsService.getByProject(organization.id, project.id, []);
          break;
      }

      setTickets(data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      showError('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [organization, project, tab]);

  // Filter tickets
  const filteredTickets = tickets.filter((ticket) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matches =
        ticket.subject.toLowerCase().includes(query) ||
        ticket.description.toLowerCase().includes(query) ||
        ticket.submitterEmail?.toLowerCase().includes(query) ||
        ticket.ticketNumber?.toString().includes(query);
      if (!matches) return false;
    }

    // Priority filter
    if (priorityFilter !== 'all' && ticket.priority !== priorityFilter) {
      return false;
    }

    // Type filter
    if (typeFilter !== 'all' && ticket.type !== typeFilter) {
      return false;
    }

    return true;
  });

  // Stats
  const stats = {
    new: tickets.filter((t) => t.status === 'new').length,
    open: tickets.filter((t) => t.status === 'open').length,
    pending: tickets.filter((t) => t.status === 'pending').length,
    urgent: tickets.filter((t) => t.priority === 'urgent').length,
  };

  const columns: Column<Ticket>[] = [
    {
      id: 'ticketNumber',
      label: '#',
      width: 70,
      render: (row) => (
        <Typography variant="body2" fontWeight="medium">
          #{row.ticketNumber || row.id.slice(0, 6)}
        </Typography>
      ),
    },
    {
      id: 'subject',
      label: 'Subject',
      render: (row) => (
        <Box>
          <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: { xs: 150, sm: 200, md: 300 } }}>
            {row.subject}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.submitterEmail}
          </Typography>
        </Box>
      ),
    },
    ...(isMobile
      ? []
      : [
          {
            id: 'type',
            label: 'Type',
            width: 100,
            render: (row: Ticket) => (
              <Chip label={TYPE_LABELS[row.type]} size="small" variant="outlined" />
            ),
          } as Column<Ticket>,
        ]),
    {
      id: 'priority',
      label: 'Priority',
      width: isMobile ? 80 : 100,
      render: (row) => (
        <Chip
          label={isMobile ? row.priority[0].toUpperCase() : row.priority}
          size="small"
          color={PRIORITY_COLORS[row.priority]}
          icon={row.priority === 'urgent' ? <PriorityHigh /> : undefined}
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      width: isMobile ? 80 : 100,
      render: (row) => (
        <Chip label={row.status} size="small" color={STATUS_COLORS[row.status]} />
      ),
    },
    ...(isTablet
      ? []
      : [
          {
            id: 'assigneeId',
            label: 'Assignee',
            width: 120,
            render: (row: Ticket) =>
              row.assigneeName ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                    {row.assigneeName[0]}
                  </Avatar>
                  <Typography variant="body2" noWrap>
                    {row.assigneeName}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Unassigned
                </Typography>
              ),
          } as Column<Ticket>,
          {
            id: 'createdAt',
            label: 'Created',
            width: 100,
            render: (row: Ticket) => (
              <Typography variant="body2" color="text.secondary">
                {formatRelativeTime(row.createdAt)}
              </Typography>
            ),
          } as Column<Ticket>,
        ]),
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Tickets
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={fetchTickets}>
            <Refresh />
          </IconButton>
          {canManageTickets && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setNewTicketOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                },
              }}
            >
              {isMobile ? 'New' : 'New Ticket'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Stats Cards - Responsive Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Badge badgeContent={stats.new} color="info">
                  <Assignment />
                </Badge>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    New
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {stats.new}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentInd color="warning" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Open
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {stats.open}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTime />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Pending
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {stats.pending}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PriorityHigh color="error" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Urgent
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {stats.urgent}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs - Scrollable on mobile */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons={isMobile ? 'auto' : false}
        sx={{ mb: 2 }}
      >
        <Tab label="Open" />
        <Tab label="Unassigned" />
        <Tab label="My Tickets" />
        <Tab label="Resolved" />
        <Tab label="All" />
      </Tabs>

      {/* Filters - Responsive */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search tickets..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 300 } }}
        />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 120 } }}>
            <Select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as TicketPriority | 'all')}
              displayEmpty
            >
              <MenuItem value="all">All Priorities</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 120 } }}>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as TicketType | 'all')}
              displayEmpty
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="bug">Bug</MenuItem>
              <MenuItem value="support">Support</MenuItem>
              <MenuItem value="feedback">Feedback</MenuItem>
              <MenuItem value="feature_request">Feature</MenuItem>
              <MenuItem value="question">Question</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Tickets Table */}
      <DataTable
        columns={columns}
        data={filteredTickets}
        loading={loading}
        emptyMessage="No tickets found"
        onRowClick={(ticket) => navigate(`/app/tickets/${ticket.id}`)}
        selectable={canManageTickets && !isMobile}
        onDelete={
          canManageTickets && !isMobile
            ? async (items) => {
                if (!confirm(`Delete ${items.length} ticket(s)? This cannot be undone.`)) return;
                try {
                  await Promise.all(items.map((ticket) => ticketsService.delete(ticket.id)));
                  showSuccess(`${items.length} ticket(s) deleted`);
                  fetchTickets();
                } catch {
                  showError('Failed to delete some tickets');
                }
              }
            : undefined
        }
      />

      {/* New Ticket Dialog */}
      <NewTicketDialog
        open={newTicketOpen}
        onClose={() => setNewTicketOpen(false)}
        onCreated={fetchTickets}
      />
    </Box>
  );
};
